import { Chip, Skeleton, Stack, styled, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useSlideLabels } from 'components/StudiesDashboard/StudySettings/StudyLabel/useSlideLabels';
import { flatMap, isEmpty, join, map, partition, reduce, size, times, uniq } from 'es-toolkit/compat';
import { Permission } from 'interfaces/permissionOption';
import { Procedure } from 'interfaces/procedure';
import { Slide, SlideQcLabel } from 'interfaces/slide';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import pluralize from 'pluralize';
import React, { useMemo, useState } from 'react';
import { formatDateTimeForUser } from 'utils/dateTime';
import { getProcedureIdentifier, getStainStrWithNegPosControl } from 'utils/helpers';
import { usePermissions } from 'utils/usePermissions';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { CardMode } from '.';
import ApprovalWidget from '../ApprovalWidget';
import { useCurrentProcedurePageCardsPerRow } from '../useProcedurePageColumns';
import SlideCarousel from './Carousel/SlideCarousel';
import {
  defaultSlideCarouselPresentation,
  previewSlideCarouselPresentation,
} from './Carousel/slideCarouselPresentation';
import SlideMultiplexMarkers from './SlideMultiplexMarkers';

export const StyledCard = styled(Card)(() => ({
  borderRadius: '15px',

  '&:hover': {
    boxShadow: '0px 0px 2px 0px rgba(0,0,0,0.2)',
  },

  'a:hover': {
    color: 'inherit',
  },
}));

export const StyledCardContent = styled(CardContent)(
  ({
    theme: {
      palette: { mode, grey, text },
      spacing,
    },
  }) => ({
    color: mode === 'light' ? grey[700] : grey[200],
    padding: spacing(1.5, 2, 1.75),
    '&:last-child': {
      paddingBottom: spacing(1.75),
    },
    '&:hover': {
      color: mode === 'light' ? text.primary : text.secondary,
    },
  })
);

const MetaEyebrowTypography = styled(Typography)(({ theme }) => ({
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  lineHeight: 1.2,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
}));

const MetaValueTypography = styled(Typography)(() => ({
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: 1.35,
  minWidth: 0,
}));

const CompactChip = styled(Chip)(({ theme }) => ({
  maxWidth: '100%',
  height: 22,
  borderRadius: 999,
  '& .MuiChip-label': {
    display: 'block',
    overflow: 'hidden',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
}));

const lineClampSx = (lineCount: number): Record<string, string | number> => ({
  display: '-webkit-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lineCount,
});

const MAX_BADGES_PER_GROUP = 3;

interface Props {
  procedure: Procedure;
  galleryView?: boolean;
  mode?: CardMode;
  forceDisplayCheckbox?: boolean;
  selectable?: boolean;
}

export const NOT_AVAILABLE = 'N/A';
export const usesCompactProcedureCardLayout = (cardsPerRow: number) => cardsPerRow >= 5;

const stainCountMap = (stains: string[]) => {
  return reduce(
    stains,
    (record: Record<string, number>, cur) => {
      record[cur] = (record[cur] || 0) + 1;
      return record;
    },
    {}
  );
};

const renderOverflowChip = (hiddenCount: number, color: 'default' | 'primary' | 'secondary' = 'default') => {
  if (hiddenCount <= 0) {
    return null;
  }

  return <CompactChip label={`+${hiddenCount}`} size="small" color={color} variant="outlined" />;
};

export const CaseCardContents: React.FunctionComponent<
  React.PropsWithChildren<
    Props & {
      currentVisibleSlide: Slide;
      setCurrentVisibleSlide?: React.Dispatch<React.SetStateAction<Slide>>;
    }
  >
> = ({
  children,
  procedure,
  galleryView,
  currentVisibleSlide,
  setCurrentVisibleSlide,
  mode,
  forceDisplayCheckbox,
  selectable,
}) => {
  const theme = useTheme();
  const currentCardsPerRow = useCurrentProcedurePageCardsPerRow();
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName();

  const { hasPermission } = usePermissions();
  const canApproveCases = hasPermission(Permission.ApproveCases);
  const canViewInternalLabels = hasPermission(Permission.ViewInternalSlideLabels);
  const isCurrentSlideMultiplex = currentVisibleSlide?.stainingType === MULTIPLEX_STAIN_ID;
  const [markersPopoverDismissToken, setMarkersPopoverDismissToken] = useState(0);
  const [isMarkersPopoverOpen, setIsMarkersPopoverOpen] = useState(false);

  const stains = flatMap(procedure.slides, (slide) => {
    const stainingType = slide.stainingType;
    const negativeControl = slide.negativeControl;
    const positiveControl = slide.positiveControl;

    return getStainStrWithNegPosControl(stainTypeIdToDisplayName(stainingType), negativeControl, positiveControl);
  });

  const stainCount = stainCountMap(stains);

  const stainsDisplayArray = map(stains, (stain) => {
    return stainCount[stain] > 1 ? `${stain} (${stainCount[stain]})` : stain;
  });

  const uniqueStains = uniq(stainsDisplayArray);

  const stainsDisplay = isLoadingStainTypeOptions ? 'Loading...' : join(uniqueStains, ', ');

  const allQcLabels: SlideQcLabel[] = currentVisibleSlide?.qcLabels;
  const tags = map(currentVisibleSlide?.slideTagAssignments, 'slideMetadataTag');
  const [labelsWithError, successfullyCalcLabels] = partition(allQcLabels, (qcLabel) => qcLabel?.errorCalculatingLabel);
  const showLabelsErrorChip = canViewInternalLabels && !isEmpty(labelsWithError);
  const [showLabelsErrorInfo, setShowLabelsErrorInfo] = useState(false);

  const { isLoading: isExternalLabelsLoading, getLabelDisplayName } = useSlideLabels();

  const skeletonChipSizes = useMemo(() => times(2, () => getRandomChipSize()), []);

  const numSlides = size(procedure?.slides);
  const indicationDisplay = procedure?.diagnosis || procedure?.clinicalData?.primaryDiagnosis || NOT_AVAILABLE;
  const ingestedDate = procedure?.createdAt ? formatDateTimeForUser(procedure.createdAt) : NOT_AVAILABLE;
  const currentSlideIsNegativeControl = Boolean(currentVisibleSlide?.negativeControl);
  const useCompactCardLayout = usesCompactProcedureCardLayout(currentCardsPerRow);
  const handleCardClickCapture = (event: React.MouseEvent<HTMLElement>) => {
    if (!isMarkersPopoverOpen) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setIsMarkersPopoverOpen(false);
    setMarkersPopoverDismissToken((currentDismissToken) => currentDismissToken + 1);
  };
  const visibleLabels = successfullyCalcLabels.slice(0, MAX_BADGES_PER_GROUP);
  const visibleTags = tags.slice(0, MAX_BADGES_PER_GROUP);

  return (
    <Box onClickCapture={handleCardClickCapture} sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
      <Box sx={{ flex: '0 0 auto' }}>
        <SlideCarousel
          caseData={procedure}
          slides={procedure.slides}
          onChangeSlide={numSlides > 1 && setCurrentVisibleSlide}
          presentation={galleryView ? previewSlideCarouselPresentation : defaultSlideCarouselPresentation}
          selectable={selectable}
          forceDisplayCheckbox={forceDisplayCheckbox}
          mode={mode}
        >
          {children}
        </SlideCarousel>
      </Box>

      {!galleryView && (
        <StyledCardContent
          sx={{
            p: useCompactCardLayout ? 1 : undefined,
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            minHeight: 0,
            '&:last-child': {
              pb: useCompactCardLayout ? 1 : undefined,
            },
          }}
        >
          <Stack
            data-testid="case-card-main-content"
            spacing={useCompactCardLayout ? 0.875 : 1.25}
            sx={{ flex: '1 1 auto', minHeight: 0 }}
          >
            <Stack spacing={useCompactCardLayout ? 0.5 : 0.75}>
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{
                  fontWeight: theme.typography.fontWeightBold,
                  lineHeight: 1.2,
                  ...lineClampSx(1),
                }}
              >
                {getProcedureIdentifier(procedure)}
              </Typography>

              <Stack direction="row" spacing={useCompactCardLayout ? 0.5 : 0.75} useFlexGap flexWrap="wrap">
                <CompactChip label={`${numSlides} ${pluralize('slide', numSlides)}`} size="small" variant="outlined" />
                {isCurrentSlideMultiplex ? (
                  <SlideMultiplexMarkers
                    slide={currentVisibleSlide}
                    studyId={procedure.studyId}
                    caseId={procedure.id}
                    dismissToken={markersPopoverDismissToken}
                    onOpenChange={setIsMarkersPopoverOpen}
                  />
                ) : uniqueStains.length ? (
                  <CompactChip
                    label={`${uniqueStains.length} ${pluralize('stain', uniqueStains.length)}`}
                    size="small"
                    variant="outlined"
                  />
                ) : null}
                {currentSlideIsNegativeControl ? (
                  <CompactChip
                    label="Negative control"
                    size="small"
                    color="warning"
                    sx={{
                      '& .MuiChip-label': {
                        fontWeight: theme.typography.fontWeightBold,
                      },
                    }}
                  />
                ) : null}
              </Stack>
            </Stack>

            <Box
              data-testid="case-card-metadata-grid"
              data-compact-layout={useCompactCardLayout ? 'true' : 'false'}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                columnGap: useCompactCardLayout ? 1 : 1.5,
                rowGap: useCompactCardLayout ? 0.75 : 1,
              }}
            >
              <Box sx={{ minWidth: 0, gridColumn: useCompactCardLayout ? 'auto' : '1 / -1' }}>
                <MetaEyebrowTypography>Indication</MetaEyebrowTypography>
                <MetaValueTypography sx={lineClampSx(useCompactCardLayout ? 1 : 2)} title={indicationDisplay}>
                  {indicationDisplay}
                </MetaValueTypography>
              </Box>

              <Box sx={{ minWidth: 0, gridColumn: useCompactCardLayout ? 'auto' : '1 / -1' }}>
                <MetaEyebrowTypography>Stain</MetaEyebrowTypography>
                <MetaValueTypography
                  sx={lineClampSx(useCompactCardLayout ? 1 : 2)}
                  title={numSlides ? stainsDisplay : NOT_AVAILABLE}
                >
                  {numSlides ? stainsDisplay : NOT_AVAILABLE}
                </MetaValueTypography>
              </Box>

              {!useCompactCardLayout && (
                <Box sx={{ minWidth: 0 }}>
                  <MetaEyebrowTypography>Processed</MetaEyebrowTypography>
                  <MetaValueTypography sx={lineClampSx(1)}>{ingestedDate}</MetaValueTypography>
                </Box>
              )}

              {procedure?.comments && !useCompactCardLayout ? (
                <Box sx={{ minWidth: 0, gridColumn: useCompactCardLayout ? 'auto' : '1 / -1' }}>
                  <MetaEyebrowTypography>Comments</MetaEyebrowTypography>
                  <MetaValueTypography sx={lineClampSx(2)}>{procedure.comments}</MetaValueTypography>
                </Box>
              ) : null}
            </Box>

            {!isEmpty(successfullyCalcLabels) || showLabelsErrorChip || isExternalLabelsLoading ? (
              <Stack spacing={0.5}>
                <MetaEyebrowTypography>Labels</MetaEyebrowTypography>

                {isExternalLabelsLoading ? (
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {map(skeletonChipSizes, (chipSize, index) => (
                      <Skeleton
                        key={index}
                        variant="rectangular"
                        width={chipSize}
                        height={22}
                        sx={{ borderRadius: 999 }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <>
                    <Stack direction="row" spacing={useCompactCardLayout ? 0.5 : 0.75} useFlexGap flexWrap="wrap">
                      {map(visibleLabels, (label: SlideQcLabel) => (
                        <CompactChip
                          key={label.text}
                          label={getLabelDisplayName(label.text)}
                          color="primary"
                          size="small"
                        />
                      ))}
                      {renderOverflowChip(successfullyCalcLabels.length - visibleLabels.length, 'primary')}
                      {showLabelsErrorChip && (
                        <Tooltip title={showLabelsErrorInfo ? 'click to see less info' : 'click to see more info'}>
                          <CompactChip
                            onClick={(event) => {
                              event.preventDefault();
                              setShowLabelsErrorInfo((prevShowLabelsErrorInfo) => !prevShowLabelsErrorInfo);
                            }}
                            clickable
                            label="!"
                            size="small"
                            color="default"
                          />
                        </Tooltip>
                      )}
                    </Stack>

                    {showLabelsErrorInfo && (
                      <Typography variant="caption" color="warning.main">
                        {`Error calculating ${pluralize('label', labelsWithError.length)}: ${join(
                          map(labelsWithError, (label) => getLabelDisplayName(label.text)),
                          ', '
                        )}`}
                      </Typography>
                    )}
                  </>
                )}
              </Stack>
            ) : null}

            {canViewInternalLabels && tags?.length ? (
              <Stack spacing={0.5}>
                <MetaEyebrowTypography>Tags</MetaEyebrowTypography>

                <Stack direction="row" spacing={useCompactCardLayout ? 0.5 : 0.75} useFlexGap flexWrap="wrap">
                  {map(visibleTags, (tag) => (
                    <CompactChip key={tag.id} label={tag.tagValue} color="secondary" size="small" />
                  ))}
                  {renderOverflowChip(tags.length - visibleTags.length, 'secondary')}
                </Stack>
              </Stack>
            ) : null}
          </Stack>

          {canApproveCases && <ApprovalWidget procedure={procedure} />}
        </StyledCardContent>
      )}
    </Box>
  );
};

function getRandomChipSize() {
  return Math.random() * 16 + 52;
}
